import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageSquare, ArrowLeft, Loader2, Plus, Lock } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loading, EmptyState } from "@/components/common/Loading";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { ChatMessageText } from "@/components/social/ChatMessageText";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";
import { AuthorLink } from "@/components/social/AuthorLink";
import { OfficialBadge } from "@/components/social/OfficialBadge";
import { useStaffMessaging } from "@/hooks/useStaffMessaging";
import { timeAgo, fullTime, messageStamp } from "@/lib/timeago";
import { cn } from "@/lib/utils";

/** Find an eligible member and start their official conversation. Searches the public
 *  People directory, which is the only member search every authorized staff member (admin
 *  or not) is allowed to use. */
function NewOfficialMessage({ onStarted }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setResults(null);
      try {
        const { data } = await api.get(`/directory/people?limit=12${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`);
        setResults(Array.isArray(data) ? data : (data.items || []));
      } catch (e) { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const start = async (m) => {
    setBusyId(m.user_id);
    try {
      const { data } = await api.post("/staff-messaging/conversations", { target_user_id: m.user_id });
      setOpen(false); setQ("");
      onStarted(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the conversation");
    } finally { setBusyId(null); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="shrink-0" data-testid="staff-new-message-button">
        <Plus className="mr-1.5 h-4 w-4" /> New official message
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-new-message-dialog">
          <DialogHeader>
            <DialogTitle>New official message</DialogTitle>
            <DialogDescription>Find a member — the conversation will be sent as Startup Otter.</DialogDescription>
          </DialogHeader>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" data-testid="staff-member-search" />
          <ScrollArea className="max-h-72">
            {results === null ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : results.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="staff-member-search-empty">No matching members.</div>
            ) : (
              <ul className="divide-y divide-border">
                {results.map((m) => (
                  <li key={m.user_id}>
                    <button onClick={() => start(m)} disabled={busyId === m.user_id}
                            className="flex w-full items-center gap-3 px-1 py-2.5 text-left transition-colors hover:bg-secondary/60 disabled:opacity-60"
                            data-testid={`staff-member-${m.user_id}`}>
                      <Avatar className="h-9 w-9">
                        {m.headshot_url && <AvatarImage src={fileUrl(m.headshot_url)} alt="" />}
                        <AvatarFallback className="bg-teal-50 text-teal-700">{(m.display_name || "?")[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-[600] text-foreground">{m.display_name || "Member"}</div>
                        {m.headline && <div className="truncate text-xs text-muted-foreground">{m.headline}</div>}
                      </div>
                      {busyId === m.user_id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Staff Messaging V1 — the SHARED official inbox. Every authorized staff member sees every
 *  official conversation; there are no assignments. Ordinary personal conversations can
 *  never appear here (the API only returns threads on the official channel). */
export default function StaffInbox() {
  const { officialAvatarUrl } = useStaffMessaging();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [actors, setActors] = useState({});
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [denied, setDenied] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadConvos = useCallback(async () => {
    try {
      const { data } = await api.get("/staff-messaging/conversations");
      setConvos(data);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 404) setDenied(true);
      setConvos([]);
    }
  }, []);

  // Selecting only moves the URL; the effect below does the single fetch. Keeping these
  // separate is what stops a click from loading the thread twice.
  const selectConvo = useCallback((id) => setParams({ c: id }), [setParams]);

  const openingRef = useRef(null);

  const openConvo = useCallback(async (id) => {
    // One fetch per thread open, even when the effect runs twice (React StrictMode
    // double-invokes effects in development).
    if (openingRef.current === id) return;
    openingRef.current = id;
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/staff-messaging/conversations/${id}/messages`);
      setActive({ id, recipient: data.recipient });
      setMessages(data.messages || []);
      setActors(data.actors || {});
      loadConvos();
    } catch (e) {
      toast.error("Could not open the conversation");
    } finally { setLoadingThread(false); openingRef.current = null; }
  }, [loadConvos]);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => {
    if (activeId && (!activeRef.current || activeRef.current.id !== activeId)) openConvo(activeId);
    if (!activeId) { setActive(null); setMessages([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Live fan-out: any staff member's send, any member's reply, and team-read updates.
  useRealtimeEvent((evt) => {
    if (evt.type === "staff_message") {
      const cur = activeRef.current;
      if (cur && evt.data.conversation_id === cur.id) {
        setMessages((prev) => (prev.find((m) => m.id === evt.data.message.id) ? prev : [...prev, evt.data.message]));
        api.post(`/staff-messaging/conversations/${cur.id}/read`).catch(() => {});
      }
      loadConvos();
    }
    if (evt.type === "staff_thread_read" || evt.type === "staff_messages_read") loadConvos();
  });

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body || !active) return;
    setSending(true); setText("");
    try {
      const { data } = await api.post(`/staff-messaging/conversations/${active.id}`, { body });
      setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
      loadConvos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
      setText(body);
    } finally { setSending(false); }
  };

  if (denied) {
    return <EmptyState icon={Lock} title="Staff messaging only"
                       description="You don't have access to the shared Startup Otter inbox."
                       testId="staff-inbox-forbidden" />;
  }
  if (convos === null) return <Loading />;

  const total = convos.reduce((n, c) => n + (c.unread || 0), 0);

  return (
    <div data-testid="staff-inbox">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-[680] tracking-[-0.01em] text-foreground">
            Staff Inbox <OfficialBadge label="Startup Otter" />
          </h1>
          <p className="mt-1 text-muted-foreground">
            One shared inbox. Every message you send here goes out as Startup Otter.
            {total > 0 && <span className="ml-1 font-[600] text-primary" data-testid="staff-inbox-unread-total">{total} unread</span>}
          </p>
        </div>
        <NewOfficialMessage onStarted={selectConvo} />
      </div>

      <Card className="mt-6 grid h-[64vh] grid-cols-1 grid-rows-1 overflow-hidden lg:grid-cols-12">
        <div className={cn("min-h-0 overflow-hidden border-border lg:col-span-4 lg:border-r", active && "hidden lg:block")}>
          <ScrollArea className="h-full">
            {convos.length === 0 ? (
              <div className="p-6"><EmptyState icon={MessageSquare} title="No official conversations"
                description="Start one from a member's profile or with New official message." testId="staff-inbox-empty" /></div>
            ) : (
              <ul className="divide-y divide-border">
                {convos.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => selectConvo(c.id)}
                            className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60",
                              active && active.id === c.id && "bg-teal-50/60")}
                            data-testid={`staff-convo-${c.id}`}>
                      <Avatar className="h-11 w-11">
                        {c.recipient?.headshot_url && <AvatarImage src={fileUrl(c.recipient.headshot_url)} alt="" />}
                        <AvatarFallback className="bg-teal-50 text-teal-700">{(c.recipient?.display_name || "?")[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-[600] text-foreground">{c.recipient?.display_name || "Member"}</span>
                          {c.last_message_at && <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(c.last_message_at)}</span>}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm text-muted-foreground">{c.last_message || "No messages yet"}</span>
                          {c.unread > 0 && (
                            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground"
                                  data-testid={`staff-convo-unread-${c.id}`}>{c.unread}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </div>

        <div className={cn("flex min-h-0 flex-col overflow-hidden lg:col-span-8", !active && "hidden lg:flex")}>
          {!active ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <EmptyState icon={MessageSquare} title="Select a conversation" description="Choose a member to read or reply as Startup Otter." />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <button className="lg:hidden" onClick={() => { setActive(null); setParams({}); }} aria-label="Back" data-testid="staff-thread-back"><ArrowLeft className="h-5 w-5" /></button>
                <Avatar className="h-9 w-9">
                  {active.recipient?.headshot_url && <AvatarImage src={fileUrl(active.recipient.headshot_url)} alt="" />}
                  <AvatarFallback className="bg-teal-50 text-sm text-teal-700">{(active.recipient?.display_name || "?")[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 font-[620] text-foreground" data-testid="staff-thread-title">
                  <AuthorLink card={active.recipient} className="text-foreground hover:underline" fallback="Member" testId="staff-thread-title-link" />
                </div>
                <OfficialBadge label="Replying as Startup Otter" testId="staff-thread-identity" />
              </div>

              <ScrollArea className="min-h-0 flex-1 px-4 py-4">
                {loadingThread ? <Loading /> : (
                  <div className="space-y-2.5">
                    {messages.map((m) => {
                      const official = m.official;
                      const actor = m.actor_user_id ? actors[m.actor_user_id] : null;
                      return (
                        <div key={m.id} className={cn("flex items-end gap-2", official ? "flex-row-reverse" : "flex-row")} data-testid={`staff-msg-${m.id}`}>
                          <Avatar className="h-8 w-8">
                            {official
                              ? (officialAvatarUrl && <AvatarImage src={fileUrl(officialAvatarUrl)} alt="Startup Otter" />)
                              : (active.recipient?.headshot_url && <AvatarImage src={fileUrl(active.recipient.headshot_url)} alt="" />)}
                            <AvatarFallback className="bg-teal-50 text-[11px] text-teal-700">
                              {official ? "SO" : (active.recipient?.display_name || "?")[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className={cn("flex min-w-0 flex-col", official ? "items-end" : "items-start")}>
                            <div className={cn("max-w-[75%] rounded-2xl px-3.5 py-2 text-[14px] leading-6",
                                official ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-secondary text-foreground")}
                                 title={fullTime(m.created_at)}>
                              <ChatMessageText body={m.body} />
                            </div>
                            <span className="px-1 text-[10px] text-muted-foreground" data-testid={`staff-msg-time-${m.id}`}>
                              {messageStamp(m.created_at)}
                              {official && actor && <span data-testid={`staff-msg-actor-${m.id}`}> · sent by {actor.display_name}</span>}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                )}
              </ScrollArea>

              <div className="flex items-center gap-2 border-t border-border p-3">
                <EmojiPickerButton targetRef={inputRef} value={text} onChange={setText} testId="staff-message-emoji-button" />
                <Input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                       placeholder="Reply as Startup Otter…" data-testid="staff-message-input" />
                <Button onClick={send} disabled={sending || !text.trim()} data-testid="staff-message-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
